import CancellationToken from "cancellationtoken";

import { BaseService } from "./BaseService";
import { IDataRow, IODataResult, IFormData } from "Entities";
import { PageContext, QueryParameter, ApplicationParameter } from "Core";
import { Configuration } from "Configuration";
import {
    ContextParameter,
    GridDataClient,
    CommandRequestParameter,
    CommandContextParameter,
    PageTokenParameter,
    GridCommandRequestParameter,
    GridCommandClient,
    GridCommandOption,
    CommandResult,
    CommandDataRowContextParameter,
    CommandClient,
    CommandMultipleDataRowContextParameter,
    ExecuteResult,
    CommandContextTreeParameter,
    GridDataTreeClient,
    ApiException,
    BaseResult,
    ColumnSelectorExecuteResult,
    CommandBoardDataRowContextParameter,
    BoardCommandDataRow,
} from "./Client";

/**
 * Provides methods for accessing the command-specific services on the server.
 * @hidden
 */
export class CommandService extends BaseService {
    /**
     * Create the request parameter for PageToken.
     */
    public static getPageTokenParameter(): PageTokenParameter {
        let params: PageTokenParameter = {
            PageToken: PageContext.getPageToken(),
        };
        return params;
    }

    /**
     * Creates the request parameters for context.
     */
    public static getContextParameters(): ContextParameter {
        let params: ContextParameter = {
            QueryParameters: null,
            PageToken: null,
            ApplicationParameters: null,
        };

        params.QueryParameters = QueryParameter.getAll();

        params.ApplicationParameters = ApplicationParameter.getAll();

        // Set page token
        params.PageToken = PageContext.getPageToken();

        return params;
    }

    /**
     * Creates the request parameters for context.
     */
    public getContextParameters(): ContextParameter {
        return CommandService.getContextParameters();
    }

    /**
     * Creates the request parameters for access to the Command-Service.
     * Includes the Filter, QueryParameters, ApplicationParameters and the current page token.
     */
    public static getRequestParameters(): CommandContextParameter {
        let params = <CommandContextParameter>CommandService.getContextParameters();

        // Load Filter parameters
        params.Filter = PageContext.getFilterParameters();
        return params;
    }

    /**
     * Performs the get-operation for grid-data.
     * @param url Url to the service.
     * @param gridId ID of the Grid.
     * @param options Request Options (i.e. filter, sort, top, take,...)
     */
    public get(gridId: number, options: any, token: CancellationToken, rowGuid?: string, parentRowGuid?: string): Promise<CommandResult> {
        let params = <CommandRequestParameter>$.extend(CommandService.getRequestParameters(), { options: options });
        if (rowGuid) {
            params.RowGuid = rowGuid;
        }
        if (parentRowGuid) {
            params.ParentRowGuid = parentRowGuid;
        }
        let client = new GridDataClient(Configuration.baseUrl);
        client.beforeSend = this.getBeforeSend();
        // client.get(gridId, param)
        return this.handleResult(
            new Promise<CommandResult>((resolve, reject) => {
                let xhr = client.get(
                    gridId,
                    params,
                    (res) => resolve(res),
                    (exception, reason) => reject(exception)
                );
                token.onCancelled((reason) => {
                    xhr.abort();
                    reject(reason);
                });
            })
        );
    }

    /**
     * Performs the get-operation for grid-data.
     * @param url Url to the service.
     * @param gridId ID of the Grid.
     * @param options Request Options (i.e. filter, sort, top, take,...)
     */
    public getFilter(gridId: number, controlId: number, options: any): Promise<CommandResult> {
        let params = <CommandContextParameter>$.extend(CommandService.getRequestParameters(), { options: options });

        let client = new CommandClient(Configuration.baseUrl);
        client.beforeSend = this.getBeforeSend();
        return this.handleResult(
            new Promise<CommandResult>((resolve, reject) => {
                client.getFilter(
                    gridId,
                    controlId,
                    params,
                    (res) => resolve(res),
                    (exc, res) => reject(exc)
                );
            })
        );
    }

    /**
     * Get data for given form.
     * @param url Configured url for this service.
     * @param gridId GridID of the form.
     */
    public getFormData(url: string, gridId: number): Promise<IFormData> {
        return this.handleResult(
            new Promise<IFormData>((resolve, reject) => {
                let params = <CommandContextParameter>(
                    $.extend(CommandService.getRequestParameters(), { options: { reloadFromDataSource: true } })
                );
                const client = new GridDataClient(Configuration.baseUrl);
                client.beforeSend = this.getBeforeSend();
                client.get(
                    gridId,
                    params,
                    (serviceResult) => {
                        if (!serviceResult.Error && serviceResult.value && serviceResult.value.length == 1) {
                            const result = serviceResult.value[0] as IFormData;
                            resolve(result);
                        } else {
                            let error = "";
                            if (serviceResult.Error) {
                                error = serviceResult.Error;
                            } else if (serviceResult.value.length == 0) {
                                error = "No row returned";
                            } else if (serviceResult.value.length > 1) {
                                error = "Too many rows returned";
                            }
                            const result: BaseResult = {
                                Error: error,
                            };
                            reject(new ApiException(error, 500, JSON.stringify(result), {}, null));
                        }
                    },
                    (exception, reason) => reject(exception)
                );
            })
        );
    }

    /**
     * Performs the get-operation for hierarchical grid-data.
     * @param url Url to the service.
     * @param gridId ID of the Grid.
     * @param rowGuid Guid of the current node.
     * @param parentRowGuid Guid of the parent node.
     */
    public getTree(url: string, gridId: number, rowGuid?: string, parentRowGuid?: string): Promise<IODataResult> {
        let params = <CommandContextTreeParameter>CommandService.getRequestParameters();
        if (rowGuid != null) {
            params.RowGuid = rowGuid;
        }
        if (parentRowGuid != null) {
            params.ParentRowGuid = parentRowGuid;
        }

        let client = new GridDataTreeClient(Configuration.baseUrl);
        client.beforeSend = this.getBeforeSend();
        // client.get(gridId, param)
        return this.handleResult(
            new Promise<CommandResult>((resolve, reject) => {
                /*let xhr =*/

                client.get(
                    gridId,
                    params,
                    (res) => resolve(res),
                    (exception, reason) => reject(exception)
                );
                // token.onCancelled(reason => {
                //     xhr.abort();
                //     reject(reason);
                // });
            })
        );
    }

    /**
     * Executes a gridCommand.
     * @param gridId ID of the Grid.
     * @param options Options for the gridCommand.
     * @param dataRow Current dataRow.
     */
    public async gridCommand(
        gridId: number,
        options: GridCommandOption[],
        dataRow: IDataRow
    ): Promise<{ [gridCommandId: number]: IODataResult }> {
        let param: GridCommandRequestParameter = $.extend(CommandService.getRequestParameters(), {
            DataRow: { ...dataRow },
            GridCommandOptions: options,
        });

        delete param.DataRow.__original;
        delete param.DataRow._events;
        delete param.DataRow._handlers;
        delete param.DataRow.dirtyFields;
        let client = new GridCommandClient(Configuration.baseUrl);
        client.beforeSend = this.getBeforeSend();

        return this.handleResult(
            new Promise<{ [key: string]: CommandResult }>((resolve, reject) => {
                /*let xhr =*/ client.post(
                    gridId,
                    param,
                    (res) => resolve(res),
                    (exception, reason) => reject(exception)
                );
                // token.onCancelled((reason) => {
                //     xhr.abort();
                //     reject(reason);
                // });
            })
        );
    }

    /**
     * Get control data from server.
     * @param gridId ID of the Grid.
     * @param controlId ID of the control.
     */
    public getControl(gridId: number, controlId: number, dataRow: IDataRow): Promise<CommandResult> {
        let client = new CommandClient(Configuration.baseUrl);
        client.beforeSend = this.getBeforeSend();

        let params = <CommandContextParameter>$.extend(CommandService.getRequestParameters(), { DataRow: dataRow });
        return this.handleResult(
            new Promise<CommandResult>((resolve, reject) => {
                /*let xhr =*/
                client.get(
                    gridId,
                    controlId,
                    params,
                    (res) => resolve(res),
                    (exception, reason) => reject(exception)
                );
                // token.onCancelled(reason => {
                //     xhr.abort();
                //     reject(reason);
                // });
            })
        );
    }

    /**
     * Executes a treeLinkCommand.
     * @param treeLinkId ID of the treeLink.
     * @param dataRow Current dataRow.
     */
    public treeLinkCommand(
        gridId: number,
        treeLinkId: number,
        rowGuid?: string,
        parentRowGuid?: string,
        fromRowGuid?: string,
        fromParentRowGuid?: string
    ): Promise<any> {
        let params = <CommandContextTreeParameter>CommandService.getRequestParameters();
        if (rowGuid != null) {
            params.RowGuid = rowGuid;
        }
        if (parentRowGuid != null) {
            params.ParentRowGuid = parentRowGuid;
        }
        if (fromRowGuid != null) {
            params.FromRowGuid = fromRowGuid;
        }
        if (fromParentRowGuid != null) {
            params.FromParentRowGuid = fromParentRowGuid;
        }
        let client = new CommandClient(Configuration.baseUrl);
        client.beforeSend = this.getBeforeSend();
        return this.handleResult(
            new Promise<ExecuteResult>((resolve, reject) => {
                /*let xhr =*/

                client.treeLinkCommand(
                    gridId,
                    treeLinkId,
                    params,
                    (res) => resolve(res),
                    (exception, reason) => reject(exception)
                );
                // token.onCancelled(reason => {
                //     xhr.abort();
                //     reject(reason);
                // });
            })
        );
    }

    /**
     * Executes a controlCommand.
     * @param gridId ID of the grid.
     * @param controlId ID of the control.
     * @param dataRow Current dataRow.
     */
    public set(gridId: number, controlId: number, dataRow: IDataRow): Promise<ExecuteResult> {
        let params = <CommandDataRowContextParameter>$.extend(CommandService.getRequestParameters(), { DataRow: { ...dataRow } });
        let client = new CommandClient(Configuration.baseUrl);
        client.beforeSend = this.getBeforeSend();

        delete params.DataRow.__original;
        delete params.DataRow._events;
        delete params.DataRow._handlers;
        delete params.DataRow.dirtyFields;
        return this.handleResult(
            new Promise<ExecuteResult>((resolve, reject) => {
                client.set(
                    gridId,
                    controlId,
                    params,
                    (res) => resolve(res),
                    (exc, res) => reject(exc)
                );
            })
        );
    }

    /**
     * Executes a controlCommand.
     * @param gridId ID of the grid.
     * @param controlId ID of the control.
     * @param dataRow Current dataRow.
     */
    public setMultiple(gridId: number, controlId: number, dataRows: IDataRow[]): Promise<ExecuteResult> {
        let param: CommandMultipleDataRowContextParameter = {
            DataRows: dataRows,
        };

        let params = <CommandMultipleDataRowContextParameter>$.extend(CommandService.getRequestParameters(), param);
        let client = new CommandClient(Configuration.baseUrl);
        client.beforeSend = this.getBeforeSend();
        return this.handleResult(
            new Promise<ExecuteResult>((resolve, reject) => {
                client.setMultiple(
                    gridId,
                    controlId,
                    params,
                    (res) => resolve(res),
                    (exc, res) => reject(exc)
                );
            })
        );
    }

    /**
     * Executes a controlCommand.
     * @param gridId ID of the grid.
     * @param controlId ID of the control.
     * @param dataRow Current dataRow.
     */
    public setDataset(gridId: number, controlId: number, options: any): Promise<ExecuteResult> {
        let params = <CommandContextParameter>$.extend(CommandService.getRequestParameters(), { options: options });

        let client = new CommandClient(Configuration.baseUrl);
        client.beforeSend = this.getBeforeSend();
        return this.handleResult(
            new Promise<ExecuteResult>((resolve, reject) => {
                client.setDataset(
                    gridId,
                    controlId,
                    params,
                    (res) => resolve(res),
                    (exc, res) => reject(exc)
                );
            })
        );
    }
    
    /**
     * Executes a controlCommand.
     * @param gridId ID of the grid.
     * @param controlId ID of the control.
     * @param dataRow Current dataRow.
     */
    public setColumnSelector(gridId: number, controlId: number, dataRows: IDataRow[]): Promise<ColumnSelectorExecuteResult> {
        let param: CommandMultipleDataRowContextParameter = {
            DataRows: dataRows,
        };

        let params = <CommandMultipleDataRowContextParameter>$.extend(CommandService.getRequestParameters(), param);
        let client = new CommandClient(Configuration.baseUrl);
        client.beforeSend = this.getBeforeSend();
        return this.handleResult(
            new Promise<ColumnSelectorExecuteResult>((resolve, reject) => {
                client.setColumnSelector(
                    gridId,
                    controlId,
                    params,
                    (res) => resolve(res),
                    (exc, res) => reject(exc)
                );
            })
        );
    }

     /**
     * Executes a controlCommand.
     * @param gridId ID of the grid.
     * @param controlId ID of the control.
     * @param dataRow Current dataRow.
     */
    public setBoardCommand(gridId: number, controlId: number, dataRows: BoardCommandDataRow[]): Promise<ExecuteResult> {
        let params = <CommandBoardDataRowContextParameter>$.extend(CommandService.getRequestParameters(), { dataRows: dataRows });

        let client = new CommandClient(Configuration.baseUrl);
        client.beforeSend = this.getBeforeSend();
        return this.handleResult(
            new Promise<ExecuteResult>((resolve, reject) => {
                client.setBoardCommand(
                    gridId,
                    controlId,
                    params,
                    (res) => resolve(res),
                    (exc, res) => reject(exc)
                );
            })
        );
    }
}
